import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import pluralize from 'pluralize'
import queryString from 'query-string'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { isArray, isEmpty, isNil, isString, trim } from 'lodash'

import { Loader } from '@/components/loaders/Loader'
import { Button } from '@/components/buttons/Button'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { toast } from '@/components/toasts/toast.helpers'
import { closeModal, openModal } from '@/reducers/ui/ui.redux'
import { fetchOrganizationCustomBranding } from '@/reducers/organizations/organizationCustomBranding.actions'
import {
  deleteDesktopTokens,
  deleteOrganization,
  fetchOrganization,
  fetchOrganizationData,
  generatePassphrase,
  generateTemporaryLink,
  updateOrganizationProperties,
} from '@/reducers/organizations/organization.actions'
import {
  createUser,
  exportUsers,
  fetchUserPermissions,
  fetchUsers,
  restoreUser,
  updateUser,
} from '@/reducers/organizations/organizationUsers.actions'
import {
  createOrUpdateCloudSSOConfig,
  createOrUpdateDesktopSSOConfig,
  provisionDesktopSSO,
  fetchSSOConfig,
  fetchDesktopSSOConfig,
} from '@/reducers/auth/sso.actions'
import { ManageIntegrationCredentialsButton } from '@/views/Organizations/components/buttons/ManageIntegrationCredentialsButton'
import { ManageWebhookSubscriptionButton } from '@/views/Organizations/components/buttons/ManageWebhookSubscriptionButton'
import { ManageSlackNotificationsButton } from '@/views/Organizations/components/buttons/ManageSlackNotificationsButton'
import { ManageTeamsNotificationsButton } from '@/views/Organizations/components/buttons/ManageTeamsNotificationsButton'
import {
  SLACK_NOTIFICATIONS_MODAL_ID,
  SLACK_AUTO_OPEN_PARAM,
} from '@/views/Organizations/components/buttons/manageSlackNotifications.constants'
import { ManageNotificationSetupButton } from '@/views/Organizations/components/buttons/ManageNotificationSetupButton'
import { ManageSalesforceButton } from '@/views/Organizations/components/buttons/ManageSalesforceButton'
import { ManageSalesforceIntegrationButton } from '@/views/Organizations/components/buttons/ManageSalesforceIntegrationButton'
import Select from '@/components/forms/Select'
import { Card } from '@/components/cards/Card'
import { isOrgUnique, setOrgIdInLocalStorage } from '@/components/helpers/tableColumnHelpers'
import { EditableHeader } from '@/components/headers/EditableHeader'

import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { arrayToStr, determineAccessForUser, getUserAdjective } from './helpers'
import { UserTable } from './components/UserTable'
import { DesktopModeButtonGroup } from './components/buttons/DesktopModeButtonGroup'
import { OrganizationToggleGroup } from './components/OrganizationToggleGroup'
import { DeleteOrganizationButton } from './components/buttons/DeleteOrganizationButton'
import { AuthorizationOptions } from './components/AuthorizationOptions'
import { AddNewUserButton } from './components/buttons/AddNewUserButton'
import { BulkUserUploadButton } from './components/buttons/BulkUserUploadButton'
import { ExportUsersButton } from './components/buttons/ExportUsersButton'
import { ManageTagsLink } from './components/ManageTagsLink'
import { CustomizeBrandingButton } from './components/buttons/CustomizeBrandingButton'
import { EmailDomainBlocklistButton } from './components/buttons/EmailDomainBlocklistButton'
import { HotWordsButton } from './components/buttons/HotWordsButton'
import { MultiCallScoringWindowButton } from './components/buttons/MultiCallScoringWindowButton'
import './organizations.scss'

export const OrganizationPage = ({ flags }) => {
  const dispatch = useDispatch()
  const { users: orgUsers } = useSelector((state) => state.organizationUsers)
  const { cloudSSOConfig, desktopSSOConfig } = useSelector((state) => state.sso)
  const organizationTags = useSelector((state) => state.organizationTags)
  const organizationTagCategories = useSelector((state) => state.organizationTagCategories)
  const organization = useSelector((state) => state.organization)
  const playbooks = useSelector((state) => state.playbooks.playbooks)
  const { currentlyOpenModalId: modal } = useSelector((state) => state.ui)
  const {
    edit_users: editUsers,
    organizationid: userOrganizationId,
    org_realtime_coaching_access: orgRealtimeCoachingAccess,
    webhook_editor: webhookEditor,
  } = useSelector((state) => state.currentUser)

  const [loading, setLoading] = useState(true)
  const [organizationField, setOrganizationField] = useState({})
  const [modalTitle, setModalTitle] = useState({})
  const [userFilter, setUserFilter] = useState('active')
  const history = useHistory()
  const isBaltoAdmin = userOrganizationId === 1
  const isBaltoOrg = organization.id === 1

  // Memoise so the BulkUserUploadButton → PrepareAndPreview → validation
  // useMemo deps stay identity-stable — a fresh object literal here would
  // bust the per-row validation cache on every render of this page.
  const organizationFeatureFlags = useMemo(
    () => ({
      qa_enabled: organization.qa_enabled,
      qa_copilot_enabled: organization.qa_copilot_enabled,
      realtime_coaching_access: orgRealtimeCoachingAccess,
      desktop_enterprise_enabled: organization.desktop_enterprise_enabled,
      agent_gpt_enabled: organization.agent_gpt_enabled,
      tools_enabled: organization.tools_enabled,
      newrez_complaints_enabled: organization.newrez_complaints_enabled,
      outbound_campaigns_enabled: organization.outbound_campaigns_enabled,
      togo_enabled: organization.togo_enabled,
      screen_capture_access_enabled: organization.screen_capture_access_enabled,
    }),
    [
      organization.qa_enabled,
      organization.qa_copilot_enabled,
      orgRealtimeCoachingAccess,
      organization.desktop_enterprise_enabled,
      organization.agent_gpt_enabled,
      organization.tools_enabled,
      organization.newrez_complaints_enabled,
      organization.outbound_campaigns_enabled,
      organization.togo_enabled,
      organization.screen_capture_access_enabled,
    ]
  )
  const numOfAgents = orgUsers?.filter((user) => !user.password).length
  const numOfManagers = orgUsers?.filter((user) => user.password).length
  const { organizationid } = useParams()
  // The /users view reuses this component without an org-id route param, so fall
  // back to the signed-in user's own org. Balto admins viewing a specific org get
  // it from the route param.
  const orgIdToUse = organizationid || userOrganizationId
  const showManageMultiCallScoringButton = organization.multi_call_scoring_enabled

  useEffect(() => {
    dispatch(fetchUserPermissions(userOrganizationId))

    // SSO settings render only for Balto admins, and auth-service refuses
    // everyone else — for a customer these calls were two background 403s.
    if (isBaltoAdmin) {
      dispatch(fetchSSOConfig(orgIdToUse))
      dispatch(fetchDesktopSSOConfig(orgIdToUse))
    }
  }, [])

  // The Slack 'Add to Slack' OAuth callback redirects the browser back here
  // with ?slack=connected|error (the modal it was launched from is gone by
  // then, so the result surfaces at the page level). Toast the outcome and
  // strip the params so a refresh doesn't re-toast.
  useEffect(() => {
    const params = queryString.parse(history.location.search)
    if (isEmpty(params.slack)) {
      return
    }
    if (params.slack === 'connected') {
      toast.success('Slack connected — Balto can now DM this org’s managers.')
    } else {
      // slack_detail is read off the URL (user-controllable), so only surface
      // it when it matches the backend's short OAuth error-code shape;
      // otherwise show a generic message so a crafted link can't display
      // arbitrary/misleading text.
      const { slack_detail: detail } = params
      const safeDetail =
        typeof detail === 'string' && /^[a-z0-9_]{1,40}$/.test(detail) ? detail : ''
      toast.error(`Slack connection failed${safeDetail ? `: ${safeDetail}` : ''}`)
    }
    const { slack, slack_detail: slackDetail, ...rest } = params
    history.replace({ search: queryString.stringify(rest) })
  }, [history])

  // The Teams admin-consent callback redirects the browser back here with
  // ?teams=connected|error (+ ?teams_detail), the same way Slack does above —
  // the modal it launched from is gone after the full-page redirect, so the
  // result surfaces at the page level. Toast the outcome and strip the params
  // so a refresh doesn't re-toast.
  useEffect(() => {
    const params = queryString.parse(history.location.search)
    if (isEmpty(params.teams)) {
      return
    }
    if (params.teams === 'connected') {
      toast.success('Teams connected — Balto can now DM this org’s managers.')
    } else {
      // teams_detail is read off the URL (user-controllable), so only surface
      // it when it matches the backend's short OAuth error-code shape;
      // otherwise show a generic message so a crafted link can't display
      // arbitrary/misleading text.
      const { teams_detail: detail } = params
      const safeDetail =
        typeof detail === 'string' && /^[a-z0-9_]{1,40}$/.test(detail) ? detail : ''
      toast.error(`Teams connection failed${safeDetail ? `: ${safeDetail}` : ''}`)
    }
    const { teams, teams_detail: teamsDetail, ...rest } = params
    history.replace({ search: queryString.stringify(rest) })
  }, [history])

  // Arriving from the public Slack landing page CTA (/integrations/slack ->
  // "Connect Slack in Balto") carries ?openSlackModal=true. Open the modal so
  // the admin lands directly on the connect flow, then strip the param so a
  // refresh doesn't re-open it. Only open for users who can manage Slack — the
  // same condition that renders ManageSlackNotificationsButton below.
  useEffect(() => {
    // Wait until the org has loaded: the modal's form needs organization.id, and
    // opening before the fetch resolves hands the Slack status call an empty org
    // id (422). Guard before stripping so the param survives until we can act.
    if (isNil(organization.id)) {
      return
    }
    const params = queryString.parse(history.location.search)
    if (isEmpty(params[SLACK_AUTO_OPEN_PARAM])) {
      return
    }
    const { [SLACK_AUTO_OPEN_PARAM]: autoOpen, ...rest } = params
    history.replace({ search: queryString.stringify(rest) })
    if (!isBaltoAdmin && editUsers) {
      dispatch(openModal(SLACK_NOTIFICATIONS_MODAL_ID))
    }
  }, [organization.id, history, isBaltoAdmin, editUsers, dispatch])

  const toggleLoading = () => {
    setLoading((prevLoading) => {
      return !prevLoading
    })
  }

  const handleRestoreUser = (userId) => {
    dispatch(
      restoreUser({
        userId,
        organizationId: organization.id,
        loadUsersData: fetchUsers,
        setLoading,
        queryParam: userFilter,
      })
    )
  }

  const handleDeleteOrganization = () => {
    dispatch(
      deleteOrganization({
        organizationId: organization.id,
        push: history.push,
      })
    )
  }

  const handleDeleteDesktopTokens = () => {
    dispatch(deleteDesktopTokens({ organizationId: organization.id }))
  }

  const updateUserFilter = (filter) => {
    setUserFilter(filter)
    dispatch(
      fetchUsers({
        organizationId: organization.id,
        queryParam: filter,
        setLoading,
      })
    )
  }

  const handleUpdateOrganizationProperties = (event, value) => {
    if (event) {
      event.preventDefault()
    }

    toggleLoading()
    dispatch(
      updateOrganizationProperties({
        ...value,
        organizationId: organization.id,
        reloadOrganization: fetchOrganization,
        toggleLoading,
      })
    )
    dispatch(closeModal())
  }

  const handleUpdateDesktopAuthKey = (values) => {
    // Not handling null here because desktop key is required in FE validation. Existing orgs
    // without desktop keys won't be affected but you can not update to remove/nullify an existing key
    handleUpdateOrganizationProperties(null, { desktop_auth_key: trim(values.desktop_auth_key) })
  }

  const handleUpdateSalesforceId = (values) => {
    const updatedSalesforceId = !isEmpty(values.salesforce_id) ? trim(values.salesforce_id) : null

    handleUpdateOrganizationProperties(null, { salesforce_id: updatedSalesforceId })
  }

  const formatArrayContent = (arrayContent) => {
    let formattedArrayContent = null

    if (isArray(arrayContent)) {
      formattedArrayContent = arrayContent
    }

    if (isString(arrayContent) && !isEmpty(arrayContent)) {
      formattedArrayContent = arrayContent.split(',').map((ip) => trim(ip))
    }

    return formattedArrayContent
  }

  const handleUpdateDesktopAuthIPs = (values) => {
    const authIPsList = formatArrayContent(values.desktop_auth_ips)
    handleUpdateOrganizationProperties(null, { desktop_auth_ips: authIPsList })
  }

  const handleUpdateVoipCustomerIDBlacklist = (values) => {
    const voipCustomerIDList = formatArrayContent(values.voip_customer_id_blacklist)
    handleUpdateOrganizationProperties(null, { voip_customer_id_blacklist: voipCustomerIDList })
  }

  const handleUpdateCloudSSO = (formValues) => {
    const { metadata_url, cloud_sso_entries } = formValues

    const entriesWithMetadata = (Array.isArray(cloud_sso_entries) ? cloud_sso_entries : []).map(
      (entry) => ({
        ...entry,
        metadata_url,
      })
    )

    dispatch(
      createOrUpdateCloudSSOConfig({
        metadata_url,
        organization_id: orgIdToUse,
        cloud_sso_entries: entriesWithMetadata,
        previous_entries: cloudSSOConfig?.cloud_sso_entries || [],
      })
    )
  }

  const handleUpdateDesktopSSO = (formValues) => {
    const { desktop_sso_entries } = formValues

    dispatch(
      createOrUpdateDesktopSSOConfig({
        organization_id: orgIdToUse,
        desktop_sso_entries: Array.isArray(desktop_sso_entries) ? desktop_sso_entries : [],
        previous_entries: desktopSSOConfig?.desktop_sso_entries || [],
      })
    )
  }

  const handleProvisionDesktopSSO = (payload) => {
    // Return the dispatched thunk so the form's onSubmit can await it -- otherwise
    // Formik never resets isSubmitting and the button spins forever on error.
    return dispatch(provisionDesktopSSO({ organization_id: orgIdToUse, ...payload }))
  }

  const handleGeneratePassphrase = (updatedPassphrase) => {
    dispatch(generatePassphrase(updatedPassphrase))
  }

  const handleGenerateTemporaryLink = (expiration, user) => {
    dispatch(generateTemporaryLink(expiration, user))
  }

  const handleExportUsers = (data) => {
    dispatch(exportUsers({ organizationId: organization.id, data, toggleLoading }))
  }

  const userOptions = [
    { label: 'Active Users', value: 'active' },
    { label: 'Active Managers', value: 'managers' },
    { label: 'Deactivated Users', value: 'deleted' },
    { label: 'All Users', value: 'all' },
  ]

  const handleUpdateUser = (user, toastMessage) => {
    user.username = user.username.trim()
    dispatch(
      updateUser({
        userId: user.id,
        userData: user,
        loadUsersData: fetchUsers,
        queryParam: userFilter,
        // No-op: table refetch handles the UI update; the page-level loading spinner
        // causes a double-spinner with the table's own loading state on inline edits
        setLoading: () => {},
        toastMessage,
      })
    )
  }

  const handleUpdateUsers = () => {
    setUserFilter('active')
    dispatch(fetchUsers({ organizationId: organization.id, setLoading }))
  }

  const handleCreateUser = (user) => {
    const newUser = determineAccessForUser({
      ...user,
      username: user.username.trim().toLowerCase(),
      tags: user.tags.map((tag) => tag.id),
    })

    return dispatch(
      createUser({
        organizationId: organization.id,
        user: newUser,
        updateUsers: handleUpdateUsers,
        // No-op: the page-level spinner would unmount the open Add User modal, so a
        // failed create would lose the input anyway. The refetch has its own spinner.
        setLoading: () => {},
      })
    )
  }

  const handleModalChange = (updateOrg, modalTitle) => {
    dispatch(openModal('confirmOrganization'))
    setOrganizationField(updateOrg)
    setModalTitle(modalTitle)
  }

  useEffect(() => {
    dispatch(fetchOrganizationData({ organizationId: orgIdToUse, toggleLoading }))
    dispatch(fetchOrganizationCustomBranding(orgIdToUse))

    // Intentionally the raw route param (not orgIdToUse): this only applies when
    // a specific org is selected via the route, and must no-op on /users.
    if (isOrgUnique(organizationid)) {
      setOrgIdInLocalStorage(organizationid)
    }
  }, [])

  return (
    <>
      {modal === 'confirmOrganization' && (
        <BasicModal
          data-testid="confirm-organization-modal"
          title={modalTitle}
          onClose={() => dispatch(closeModal())}
          show={!!modal}
          size="tiny"
        >
          <p>Are you sure you want to make this change?</p>
          <div className="modal-footer">
            <Button onClick={() => dispatch(closeModal())} data-testid="cancel-change-button">
              Cancel
            </Button>
            <Button
              primary
              onClick={() => handleUpdateOrganizationProperties(null, organizationField)}
              data-testid="confirm-change-button"
            >
              Change
            </Button>
          </div>
        </BasicModal>
      )}
      <div>
        {isBaltoAdmin && loading ? (
          <Card className="no-padding">
            <div className="empty-table">
              <Loader centered />
            </div>
          </Card>
        ) : (
          <>
            <header className="page-header">
              {isBaltoAdmin ? (
                <EditableHeader
                  initialName={organization.name}
                  updateName={handleUpdateOrganizationProperties}
                />
              ) : (
                <h1 data-testid="header-name">{organization.name}</h1>
              )}

              <div>
                <div className="organization-users">
                  {pluralize(`${getUserAdjective(userFilter)} agent`, numOfAgents, true)}
                </div>
                <div className="organization-users">
                  {pluralize(`${getUserAdjective(userFilter)} manager`, numOfManagers, true)}
                </div>
              </div>
            </header>

            {isBaltoAdmin && (
              <>
                <div className="organization-options-bar" data-testid="admin-controls">
                  <DesktopModeButtonGroup
                    alwaysStartCall={organization.desktop_always_start_call}
                    visibility={organization.desktop_visibility}
                    updateOrganizationProperties={handleUpdateOrganizationProperties}
                    handleModalChange={handleModalChange}
                  />
                  <OrganizationToggleGroup
                    production={organization.production}
                    leaderboardVisible={organization.desktop_leaderboard_visibility}
                    isBaltoAdmin={isBaltoAdmin}
                    isBaltoOrg={isBaltoOrg}
                    qaEnabled={organization.qa_enabled}
                    qaCopilotEnabled={organization.qa_copilot_enabled}
                    dataAccessKey={organization.data_access_key}
                    remoteDesktopKey={organization.remote_desktop_key}
                    handleModalChange={handleModalChange}
                    rtnEnabled={organization.call_summarization_enabled}
                    audioExpiryDays={organization.audio_expiry_days}
                    postcallMinDurationMinutes={organization.postcall_min_duration_minutes}
                    postcallMaxDurationMinutes={organization.postcall_max_duration_minutes}
                    activeChannels={organization.active_channels}
                    channelIngestLimits={organization.channel_ingest_limits}
                    trackedInsightLimit={organization.tracked_insight_limit}
                    organizationId={organization.id}
                  />
                  <DeleteOrganizationButton deleteOrganization={handleDeleteOrganization} />
                </div>
                <AuthorizationOptions
                  desktopAuthKey={organization.desktop_auth_key}
                  desktopAuthIPs={organization.desktop_auth_ips}
                  organizationUuid={organization.uuid}
                  organizationName={organization.name}
                  cloudSSOConfig={cloudSSOConfig}
                  desktopSSOConfig={desktopSSOConfig}
                  handleUpdateDesktopAuthIPs={handleUpdateDesktopAuthIPs}
                  deleteDesktopTokens={handleDeleteDesktopTokens}
                  handleUpdateDesktopAuthKey={handleUpdateDesktopAuthKey}
                  handleUpdateCloudSSO={handleUpdateCloudSSO}
                  handleUpdateDesktopSSO={handleUpdateDesktopSSO}
                  handleProvisionDesktopSSO={handleProvisionDesktopSSO}
                />
              </>
            )}
            <div className="user-table-toolbar">
              <div className="flex-align-center" data-testid="customize-table-container">
                <div className="vertical-center">
                  <Select
                    data-testid="show-users-dropdown"
                    options={userOptions}
                    value={userFilter}
                    onChange={(option) => {
                      updateUserFilter(option.value)
                    }}
                    isClearable={false}
                  />
                </div>
              </div>
              <div className="organization-user-row">
                {(editUsers || isBaltoAdmin) && (
                  <AddNewUserButton
                    playbooks={playbooks}
                    tagCategories={organizationTagCategories}
                    createNewUser={handleCreateUser}
                    userOrganizationId={userOrganizationId}
                    orgRealtimeCoachingAccess={orgRealtimeCoachingAccess}
                    orgDesktopEnterpriseEnabled={organization.desktop_enterprise_enabled}
                    tags={organizationTags}
                    qaEnabled={organization.qa_enabled}
                    qaCopilotEnabled={organization.qa_copilot_enabled}
                    agentGPTEnabled={organization.agent_gpt_enabled}
                    toolsEnabled={organization.tools_enabled}
                  />
                )}
                {(editUsers || isBaltoAdmin) && (
                  <BulkUserUploadButton
                    tagCategories={organizationTagCategories}
                    setUserFilter={setUserFilter}
                    setLoading={setLoading}
                    organizationFeatureFlags={organizationFeatureFlags}
                  />
                )}
                {(editUsers || isBaltoAdmin) && (
                  <ExportUsersButton
                    playbooks={playbooks}
                    exportUsers={handleExportUsers}
                    tags={organizationTags}
                    tagCategories={organizationTagCategories}
                    organization={organization}
                  />
                )}
                <ManageTagsLink toUrl={`${history.location.pathname}/tags`} />
                {isBaltoAdmin && (
                  <HotWordsButton
                    hotWords={organization?.hot_words || []}
                    organizationId={organization.id}
                    reloadOrganization={fetchOrganization}
                    // NOTE: these flags (which control language options) would ideally
                    // belong to the org being edited, not to the editing user (which is always Org 1).
                    // No easy way to do this, so for org 1 all languages will be available. But leaving in
                    // so if this page ever was given to non org 1 no further changes are needed
                    flags={flags}
                  />
                )}
                {showManageMultiCallScoringButton && (
                  <MultiCallScoringWindowButton
                    multiCallScoringWindow={organization.multi_call_scoring_window_days}
                    organizationId={organization.id}
                    reloadOrganization={fetchOrganization}
                  />
                )}
                {isBaltoAdmin && (
                  <ManageSalesforceButton
                    handleUpdateSalesforceId={handleUpdateSalesforceId}
                    salesforceId={organization.salesforce_id}
                  />
                )}
                {isBaltoAdmin && (
                  <ManageSalesforceIntegrationButton
                    organizationId={organization.id}
                    salesforceIntegrationEnabled={organization.salesforce_integration_enabled}
                  />
                )}
                {isBaltoAdmin && (
                  <ManageIntegrationCredentialsButton organizationId={organization.id} />
                )}
                {/* Self-serve: the org must be entitled (webhooks_enabled) and the user
                    must hold the webhook_editor permission. Deliberately NOT gated on
                    isBaltoAdmin — on /users this org IS their own, and the service
                    re-checks both through authorize_webhook_editor. */}
                {organization.webhooks_enabled && webhookEditor && (
                  <ManageWebhookSubscriptionButton organizationId={organization.id} />
                )}
                {/* Self-serve Slack setup for org admins (the full creds
                    modal above stays Balto-only). On /users this org IS their
                    own; the backend re-checks via authorize_for_org. */}
                {!isBaltoAdmin && editUsers && (
                  <ManageSlackNotificationsButton organizationId={organization.id} />
                )}
                {!isBaltoAdmin && editUsers && (
                  <ManageTeamsNotificationsButton organizationId={organization.id} />
                )}
                {isBaltoAdmin && <ManageNotificationSetupButton organizationId={organization.id} />}
                {organization.custom_branding_enabled && isBaltoAdmin && (
                  <CustomizeBrandingButton organizationId={organization.id} />
                )}
                <ButtonAndFormModal
                  buttonLabel="Manage VoIP Customer ID Blacklist"
                  modalTitle="VoIP Customer ID Blacklist"
                  modalContent={
                    <p>
                      Enter VoIP customer IDs you want to blacklist, separated by{' '}
                      <strong>commas</strong>.
                    </p>
                  }
                  modalId="organizations/manageVoipCustomerIDBlacklist"
                  modalProps={{ size: 'small' }}
                  buttonProps={{ primary: true }}
                  form={
                    <AbstractForm
                      schema={[
                        {
                          name: 'voip_customer_id_blacklist',
                          type: 'textArea',
                          label: 'VoIP Customer ID Blacklist',
                          placeholder:
                            'Enter VoIP customer IDs you want to blacklist, separated by commas',
                        },
                        {
                          name: 'confirm_checkbox',
                          type: 'checkbox',
                          label: 'I want to make changes',
                          required: true,
                        },
                      ]}
                      existingValues={{
                        voip_customer_id_blacklist: arrayToStr(
                          organization.voip_customer_id_blacklist
                        ),
                      }}
                      onSubmit={handleUpdateVoipCustomerIDBlacklist}
                      buttonLabel="Save"
                      onClose={() => dispatch(closeModal())}
                      isModal
                    />
                  }
                />
                {(editUsers || isBaltoAdmin) && organization.active_channels?.includes('email') && (
                  <EmailDomainBlocklistButton organizationId={organization.id} />
                )}
              </div>
            </div>

            <UserTable
              loading={loading}
              users={orgUsers}
              playbooks={playbooks}
              tags={organizationTags}
              tagCategories={organizationTagCategories}
              editUsers={editUsers}
              isAdmin={isBaltoAdmin}
              organizationId={organization.id}
              qaEnabled={organization.qa_enabled}
              qaCopilotEnabled={organization.qa_copilot_enabled}
              agentGPTEnabled={organization.agent_gpt_enabled}
              toolsEnabled={organization.tools_enabled}
              contentCustomizationEnabled={organization.content_customization_enabled}
              handleUserUpdate={handleUpdateUser}
              generateTemporaryLink={handleGenerateTemporaryLink}
              generatePassphrase={handleGeneratePassphrase}
              orgRealtimeCoachingAccess={orgRealtimeCoachingAccess}
              orgDesktopEnterpriseEnabled={organization.desktop_enterprise_enabled}
              fetchUsers={fetchUsers}
              userFilter={userFilter}
              restoreUser={handleRestoreUser}
              flags={flags}
            />
          </>
        )}
      </div>
    </>
  )
}

export default withLDConsumer()(OrganizationPage)
